//core
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { ActionReducerMapBuilder } from "@reduxjs/toolkit/src/mapBuilders";
import { NoInfer } from "@reduxjs/toolkit/src/tsHelpers";

//service
import { agentProfileService } from "../../../services/agent/profile.service";

//actions
import {
  setGeneralProgressHide,
  setGeneralProgressShow,
  setGeneralSnackbarState,
} from "../../common/ui";
import { setUser } from "../../common/user";

// Types
export type GeneralDataType = {
  agent: {
    id: string;
    username: string;
    profile: {
      name: string;
      surname: string;
      birthday: string;
    };
    contact: {
      email: string;
      phone: string;
      messenger: {
        type: string;
        value: string;
      };
    };
    createdAt: string;
    countries: string[];
  };
};

export type ProfileSliceState = {
  general: {
    data: GeneralDataType | null;
    error: any;
    isFetching: boolean;
  };
  updatePassword: {
    data: any;
    error: any;
    isFetching: boolean;
  };
  updateEmail: {
    data: any;
    error: any;
    isFetching: boolean;
  };
};

const initialState: ProfileSliceState = {
  general: {
    data: null,
    error: null,
    isFetching: false,
  },
  updatePassword: {
    data: null,
    error: null,
    isFetching: false,
  },
  updateEmail: {
    data: null,
    error: null,
    isFetching: false,
  },
};

export const getGeneralData = createAsyncThunk(
  "agent/profile/general",
  async (_, { rejectWithValue, dispatch }) => {
    try {
      const response = await agentProfileService.getGeneralData();
      const data = await response.json();

      if (data.hasOwnProperty("authorized") && data.authorized === false) {
        dispatch(setUser(null));
        localStorage.removeItem("user");
      }

      if (!response.ok) {
        return rejectWithValue(data);
      }

      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const updatePasswordData = createAsyncThunk(
  "agent/profile/updatePassword",
  async (payload: any, { rejectWithValue, dispatch }) => {
    dispatch(setGeneralProgressShow());
    try {
      const response = await agentProfileService.updatePasswordData(payload);
      const data = await response.json();

      if (data.hasOwnProperty("authorized") && data.authorized === false) {
        dispatch(setUser(null));
        localStorage.removeItem("user");
      }

      if (response.ok && response.status === 201) {
        dispatch(
          setGeneralSnackbarState({
            open: true,
            type: "success",
            message: "success",
            messageKey: "common.messages.created",
          })
        );
      }

      if (!response.ok) {
        dispatch(setGeneralProgressHide());
        dispatch(
          setGeneralSnackbarState({
            open: true,
            type: "warning",
            message: "error",
            messageKey: "common.messages.error",
          })
        );
        return rejectWithValue(data);
      }

      dispatch(setGeneralProgressHide());

      return data;
    } catch (error) {
      dispatch(
        setGeneralSnackbarState({
          open: true,
          type: "warning",
          message: "error",
          messageKey: "common.messages.error",
        })
      );
      dispatch(setGeneralProgressHide());
      return rejectWithValue(error);
    }
  }
);

export const updateNewEmailData = createAsyncThunk(
  "agent/profile/updateNewEmail",
  async (payload: any, { rejectWithValue, dispatch }) => {
    try {
      const response = await agentProfileService.updateEmailData(payload);
      let data = await response.json();

      if (Array.isArray(data)) {
        data = {
          response: data,
          success: true,
        };
      } else {
        if (data.hasOwnProperty("authorized")) {
          dispatch(setUser(null));
          localStorage.removeItem("user");
        } else {
          data = {
            response: data,
            success: false,
          };
        }
      }

      if (!response.ok) {
        return rejectWithValue(data);
      }

      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

const profileSlice = createSlice({
  name: "agent/profile",
  initialState,
  reducers: {
    clearUpdatePasswordState: (state) => {
      state.updatePassword = initialState.updatePassword;
    },
    clearUpdateEmailState: (state) => {
      state.updateEmail = initialState.updateEmail;
    },
    clearGeneralState: (state) => {
      state.general = initialState.general;
    },
  },
  extraReducers: (
    builder: ActionReducerMapBuilder<NoInfer<ProfileSliceState>>
  ) => {
    builder.addCase(updatePasswordData.pending, (state) => {
      state.updatePassword.isFetching = true;
      state.updatePassword.error = null;
    });
    builder.addCase(updatePasswordData.fulfilled, (state, action) => {
      state.updatePassword.isFetching = false;
      state.updatePassword.data = action.payload;
    });
    builder.addCase(updatePasswordData.rejected, (state, action) => {
      state.updatePassword.isFetching = false;
      state.updatePassword.error = action.payload;
    });

    builder.addCase(updateNewEmailData.pending, (state) => {
      state.updateEmail.isFetching = true;
      state.updateEmail.error = null;
    });
    builder.addCase(updateNewEmailData.fulfilled, (state, action) => {
      state.updateEmail.isFetching = false;
      state.updateEmail.error = null;
      state.updateEmail.data = action.payload;
    });
    builder.addCase(updateNewEmailData.rejected, (state, action) => {
      state.updateEmail.isFetching = false;
      state.updateEmail.error = action.payload;
    });

    builder.addCase(getGeneralData.pending, (state) => {
      state.general.isFetching = true;
      state.general.error = null;
    });
    builder.addCase(getGeneralData.fulfilled, (state, action) => {
      state.general.isFetching = false;
      state.general.error = null;
      state.general.data = action.payload;
    });
    builder.addCase(getGeneralData.rejected, (state, action) => {
      state.general.isFetching = false;
      state.general.error = action.payload;
    });
  },
});

export const {
  clearGeneralState,
  clearUpdatePasswordState,
  clearUpdateEmailState,
} = profileSlice.actions;

export default profileSlice.reducer;
